import type { ChatterCounts, HttpPort, IChatterService, Json, RecordData } from "../contracts";

// ChatterService — wraps /api/chatter/* (foundation.communication) plus the
// record-scoped attachments list under /api/document/attachments
// (foundation.document). Backs the record strip + unified timeline
// (communication Enh 03). Which surfaces actually render for a record is
// decided server-side: the form render plan's `activity: string[]` carries
// only the declared-AND-mixin-gated components (Slice 1) — this service is
// dumb transport and never re-derives that gating.
export class ChatterService implements IChatterService {
    constructor(private readonly http: HttpPort) {}

    private base(modelKey: string, recordUuid: string): string {
        return `/api/chatter/${encodeURIComponent(modelKey)}/${encodeURIComponent(recordUuid)}`;
    }

    async listTimeline(
        modelKey: string,
        recordUuid: string,
        opts?: { page?: number; pageSize?: number; types?: string[] },
    ): Promise<RecordData[]> {
        const qs = new URLSearchParams();
        if (opts?.page != null) qs.set("page", String(opts.page));
        if (opts?.pageSize != null) qs.set("page_size", String(opts.pageSize));
        if (opts?.types?.length) qs.set("type", opts.types.join(","));
        const suffix = qs.size ? `?${qs.toString()}` : "";
        const res = await this.http.get<{ records?: RecordData[] }>(`${this.base(modelKey, recordUuid)}${suffix}`);
        return res.records ?? [];
    }

    async listFollowers(modelKey: string, recordUuid: string): Promise<RecordData[]> {
        const res = await this.http.get<{ records?: RecordData[] }>(`${this.base(modelKey, recordUuid)}/followers`);
        return res.records ?? [];
    }

    async listActivities(modelKey: string, recordUuid: string, status?: string): Promise<RecordData[]> {
        const suffix = status ? `?status=${encodeURIComponent(status)}` : "";
        const res = await this.http.get<{ records?: RecordData[] }>(
            `${this.base(modelKey, recordUuid)}/activities${suffix}`,
        );
        return res.records ?? [];
    }

    async listAttachments(modelKey: string, recordUuid: string): Promise<RecordData[]> {
        const qs = new URLSearchParams({
            model_key: modelKey,
            record_uuid: recordUuid,
            usage_kind: "record_attachment",
        });
        const res = await this.http.get<{ attachments?: RecordData[] }>(`/api/document/attachments?${qs.toString()}`);
        return res.attachments ?? [];
    }

    async isDocumentsAppVisible(modelKey: string): Promise<boolean> {
        try {
            const res = await this.http.get<{ categories?: Array<{ model_key?: string }> }>(
                "/api/document/dms/virtual/categories",
            );
            return (res.categories ?? []).some((c) => c.model_key === modelKey);
        } catch {
            return false; // no Documents app reachable → no escape hatch
        }
    }

    async previewAttachment(attachmentUuid: string): Promise<Blob> {
        return this.http.getBlob(`/api/document/attachments/${encodeURIComponent(attachmentUuid)}/preview`);
    }

    async downloadAttachment(attachmentUuid: string): Promise<Blob> {
        return this.http.getBlob(`/api/document/attachments/${encodeURIComponent(attachmentUuid)}/download`);
    }

    async follow(
        modelKey: string,
        recordUuid: string,
        payload: {
            user_id?: string;
            partner_model?: string;
            partner_id?: string;
            on_message?: boolean;
            on_email?: boolean;
            on_activity?: boolean;
            on_notification?: boolean;
        },
    ): Promise<RecordData | null> {
        const res = await this.http.post<{ record?: RecordData }>(
            `${this.base(modelKey, recordUuid)}/followers`,
            payload as Json,
        );
        return res.record ?? null;
    }

    async unfollow(followerId: string): Promise<void> {
        await this.http.delete(`/api/chatter/followers/${encodeURIComponent(followerId)}`);
    }

    async updateFollower(
        followerId: string,
        flags: Partial<{ on_message: boolean; on_email: boolean; on_activity: boolean; on_notification: boolean }>,
    ): Promise<void> {
        await this.http.patch(`/api/chatter/followers/${encodeURIComponent(followerId)}`, flags as Json);
    }

    async postMessage(modelKey: string, recordUuid: string, body: string): Promise<RecordData | null> {
        const res = await this.http.post<{ record?: RecordData }>(
            `${this.base(modelKey, recordUuid)}/message`,
            { body },
        );
        return res.record ?? null;
    }

    async postNote(modelKey: string, recordUuid: string, body: string): Promise<RecordData | null> {
        const res = await this.http.post<{ record?: RecordData }>(
            `${this.base(modelKey, recordUuid)}/note`,
            { body },
        );
        return res.record ?? null;
    }

    async scheduleActivity(
        modelKey: string,
        recordUuid: string,
        payload: {
            activity_type_code: string;
            subject: string;
            due_at?: string;
            owner_user_id?: string;
            notes?: string;
        },
    ): Promise<RecordData | null> {
        const res = await this.http.post<{ record?: RecordData }>(
            `${this.base(modelKey, recordUuid)}/activities`,
            payload as Json,
        );
        return res.record ?? null;
    }

    async uploadAttachment(modelKey: string, recordUuid: string, file: File): Promise<void> {
        // Same wire shape as the Documents app's record upload: binary body +
        // query-string back-reference onto the Attachable mixin's field.
        const bytes = await file.arrayBuffer();
        const qs = new URLSearchParams({
            model_key: modelKey,
            record_uuid: recordUuid,
            field_name: "attachment_ids",
            usage_kind: "record_attachment",
            filename: file.name,
            mimetype: file.type || "application/octet-stream",
        }).toString();
        await this.http.postBytes(`/api/document/attachments?${qs}`, bytes, file.type || "application/octet-stream");
    }

    async deleteAttachment(attachmentUuid: string): Promise<void> {
        await this.http.delete(`/api/document/attachments/${encodeURIComponent(attachmentUuid)}?soft=true`);
    }

    async counts(modelKey: string, recordUuid: string): Promise<ChatterCounts> {
        // Three independent unpaged lists; a failure in one must not blank the
        // other chips, so each falls back to an empty list on rejection.
        const [followers, activities, attachments] = await Promise.all([
            this.listFollowers(modelKey, recordUuid).catch(() => []),
            this.listActivities(modelKey, recordUuid, "planned").catch(() => []),
            this.listAttachments(modelKey, recordUuid).catch(() => []),
        ]);
        return {
            followers: followers.length,
            openActivities: activities.length,
            attachments: attachments.length,
        };
    }
}
